// ─── src/components/sections/DestinationGrid.jsx ──────────────────────────────
// DB-driven homepage destinations carousel.
// Reads from `homepage_destinations` table (admin-controlled).
// Venue counts are live from the `listings` table.
// Static src/data/destinations.js is no longer used here.
// ──────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { supabase } from "../../lib/supabaseClient";
import { track } from "../../utils/track";
import { imgUrl } from "../../utils/imgUrl.js";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// Fallback image shown when a destination has no homepage_image set.
// Card renders at 260×340 CSS px; 520×680 covers 2× retina with no waste.
const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=520&h=680&q=80";

// Fetch live venue count for a country or region.
async function fetchCount(countrySlug, regionSlug) {
  let q = supabase
    .from("listings")
    .select("*", { count: "exact", head: true })
    .eq("country_slug", countrySlug)
    .eq("status", "published")
    .eq("category", "wedding-venues");
  if (regionSlug) q = q.eq("region_slug", regionSlug);
  const { count } = await q;
  return count || 0;
}

export default function DestinationGrid({ onDestinationClick, goCountry, goRegion, goAllDestinations }) {
  const C = useTheme();
  const scrollRef = useRef(null);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: rows } = await supabase
        .from("homepage_destinations")
        .select("*")
        .eq("enabled", true)
        .order("display_order")
        .order("name");

      if (cancelled || !rows?.length) { if (!cancelled) setLoading(false); return; }

      const counts = await Promise.all(
        rows.map((d) => fetchCount(d.country_slug, d.region_slug))
      );

      if (!cancelled) {
        setDestinations(rows.map((d, i) => ({ ...d, venueCount: counts[i] })));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  // Don't render the section until we know what's there
  if (loading || destinations.length === 0) return null;

  return (
    <section
      aria-label="Explore destinations"
      className="home-dest-section"
      style={{
        position: "relative",
        background: C.card,
        padding: "100px 60px",
        overflow: "hidden",
      }}
    >
      {/* Watermark */}
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 80,
          left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "var(--font-heading-primary)",
          fontSize: "clamp(120px, 12vw, 180px)",
          fontWeight: 400,
          fontStyle: "italic",
          // color:transparent removes the text foreground entirely so
          // contrast audits do not flag this aria-hidden decorative element.
          // The subtle golden glow is preserved via textShadow.
          color: "transparent",
          textShadow: "0 0 60px rgba(211,154,33,0.10)",
          whiteSpace: "normal",
          width: "100%",
          textAlign: "center",
          pointerEvents: "none",
          userSelect: "none",
          letterSpacing: "-2px",
          lineHeight: 1,
          zIndex: 0,
        }}
      >
        Destinations
      </span>

      {/* Header row */}
      <div
        className="home-dest-header"
        style={{
          marginBottom: 48,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <div style={{ width: 28, height: 1, background: "rgba(211,154,33,0.5)" }} />
            <span style={{ fontFamily: NU, fontSize: 10, letterSpacing: "0.28em", textTransform: "uppercase", color: C.gold, fontWeight: 600 }}>
              Wedding Hotspots
            </span>
          </div>
          <h2 style={{ fontFamily: GD, fontSize: "clamp(28px, 3vw, 48px)", color: C.off, fontWeight: 400, lineHeight: 1.1 }}>
            The{" "}
            <span style={{ fontStyle: "italic", color: C.gold }}>Destinations</span>
          </h2>
          <p
            className="home-dest-sub"
            style={{ fontFamily: NU, fontSize: 14, color: C.grey, lineHeight: 1.7, maxWidth: 560, marginTop: 14, fontWeight: 300 }}
          >
            From coastal escapes to grand countryside settings, explore the
            destinations defining the modern wedding. Each chosen for its
            beauty, atmosphere, and sense of occasion.
          </p>
        </div>

        {/* Scroll arrows */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            style={{ width: 40, height: 40, borderRadius: "var(--lwd-radius-image)", border: `1px solid ${C.border}`, background: "transparent", color: C.off, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, transition: "all 0.2s" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.off; }}
          >
            ‹
          </button>
          <button
            onClick={() => scroll(1)}
            aria-label="Scroll right"
            style={{ width: 40, height: 40, borderRadius: "var(--lwd-radius-image)", border: `1px solid ${C.gold}`, background: C.gold, color: C.black, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, transition: "all 0.2s" }}
          >
            ›
          </button>
        </div>
      </div>

      {/* Horizontal scroll track */}
      <div
        ref={scrollRef}
        className="home-dest-scroll"
        style={{
          display: "flex",
          gap: 20,
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          paddingRight: 60,
          paddingBottom: 8,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`.home-dest-scroll::-webkit-scrollbar { display: none; }`}</style>

        {destinations.map((d) => {
          const img = d.homepage_image || FALLBACK_IMG;
          const isFallback = !d.homepage_image;

          return (
            <div
              key={d.id}
              role="button"
              tabIndex={0}
              aria-label={`Explore ${d.name}${d.venueCount ? `, ${d.venueCount} venues` : ""}`}
              onClick={() => {
                track("destination_click", { destination: d.name, type: d.type });
                // Navigate to destination page: /country or /country/region
                if (d.type === "region") {
                  goRegion?.(d.country_slug, d.region_slug);
                } else {
                  goCountry?.(d.country_slug);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  track("destination_click", { destination: d.name, type: d.type });
                  if (d.type === "region") {
                    goRegion?.(d.country_slug, d.region_slug);
                  } else {
                    goCountry?.(d.country_slug);
                  }
                }
              }}
              className="home-dest-card"
              style={{
                flexShrink: 0,
                width: 260,
                scrollSnapAlign: "start",
                cursor: "pointer",
                borderRadius: 6,
                overflow: "hidden",
                transition: "transform 0.4s ease, box-shadow 0.4s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.25)";
                const im = e.currentTarget.querySelector("img");
                if (im) im.style.transform = "scale(1.05)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
                const im = e.currentTarget.querySelector("img");
                if (im) im.style.transform = "scale(1)";
              }}
            >
              {/* Portrait image */}
              <div
                className="home-dest-card-img"
                style={{ width: "100%", height: 340, borderRadius: 0, overflow: "hidden", marginBottom: 14, position: "relative" }}
              >
                <img
                  src={imgUrl(img, { width: 800, quality: 75 })}
                  alt={d.name}
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s ease", display: "block" }}
                />
                {isFallback && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", letterSpacing: "0.1em", textTransform: "uppercase" }}>Image pending</span>
                  </div>
                )}
              </div>

              {/* Label */}
              <div style={{ padding: "0 10px 10px" }}>
                <div style={{ fontFamily: NU, fontSize: 15, color: C.off, fontWeight: 600, marginBottom: 4 }}>
                  {d.name}
                </div>
                {d.type === "region" && (
                  <div style={{ fontFamily: NU, fontSize: 12, color: C.grey, letterSpacing: "0.06em", marginBottom: 2 }}>
                    {d.country_name}
                  </div>
                )}
                {d.homepage_intro && (
                  <div style={{ fontFamily: NU, fontSize: 11, color: C.grey, lineHeight: 1.5, marginBottom: 4, opacity: 0.7 }}>
                    {d.homepage_intro}
                  </div>
                )}
                {/* opacity:0.4 on "Coming soon" used to fail WCAG AA (~1.8:1),
                    the blend with the section background tanked contrast even
                    though the gold itself passes fine (5.3:1) at full opacity.
                    The text content already distinguishes the two states. */}
                <div style={{ fontFamily: NU, fontSize: 11, color: C.gold, letterSpacing: "0.08em", fontWeight: 600 }}>
                  {d.venueCount > 0 ? `${d.venueCount} Venues →` : "Coming soon"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* All Destinations CTA */}
      <div style={{ marginTop: 40, display: "flex", justifyContent: "center" }}>
        <button
          onClick={() => goAllDestinations?.()}
          style={{
            fontFamily: NU,
            fontSize: 11,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            fontWeight: 600,
            padding: "16px 48px",
            border: `1px solid ${C.gold}`,
            borderRadius: 2,
            background: "transparent",
            color: C.gold,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.gold; e.currentTarget.style.color = C.black; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.gold; }}
        >
          All Destinations →
        </button>
      </div>
    </section>
  );
}
